import { BetaBadge } from '@consigli/facade';
import { useProjectId, useServiceName, useUserMeetingStatistics } from '@consigli/hooks';
import { Meeting, ServiceName } from '@consigli/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Route } from '@/routes';
import { serviceConfig, SHOW_BETA_BADGES } from '@/util/service-config';

import { MeetingDetail } from './components/meeting-detail';
import { RecentMeetings } from './recent-meeting-secretary';

const isMeetingSecretaryBeta =
  SHOW_BETA_BADGES &&
  (serviceConfig.find((s) => s.serviceType === ServiceName.MEETING_SECRETARY)?.isBeta ?? false);

const MeetingSecretaryHeader = () => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-start">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-semibold">{t('services.meeting-secretary')}</h1>
          {isMeetingSecretaryBeta && <BetaBadge />}
        </div>
        <p className="text-gray-600 mt-2">{t('meeting-secretary.header.description')}</p>
      </div>
    </div>
  );
};

const MetricCard = ({
  title,
  description,
  value,
  isLoading = false,
  isError = false,
  onClick,
}: {
  title: string;
  description: string;
  value: string | number;
  isLoading?: boolean;
  isError?: boolean;
  onClick?: () => void;
}) => {
  const isClickable = !!onClick;

  const content = (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {isLoading ? (
        <div className="h-9 w-16 bg-gray-200 rounded animate-pulse mb-2" />
      ) : (
        <div className={`text-3xl font-bold mb-2 ${isError ? 'text-red-600' : 'text-gray-900'}`}>
          {isError ? 'Error' : value}
        </div>
      )}
      {isLoading ? (
        <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
      ) : (
        <p className={`text-sm ${isError ? 'text-red-600' : 'text-gray-600'}`}>
          {isError ? 'Failed to load data' : description}
        </p>
      )}
    </div>
  );

  const baseClassName = `bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow duration-200 ease-in-out ${
    isError ? 'border-red-200 bg-red-50' : 'border-gray-200'
  }`;

  if (isClickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClassName} cursor-pointer text-left w-full`}
      >
        {content}
      </button>
    );
  }

  return <div className={baseClassName}>{content}</div>;
};

const MetricsCards = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const navigate = useNavigate();
  const { statistics, isLoading, isError } = useUserMeetingStatistics(projectId);
  const showSkeleton = isLoading && statistics == null;

  const handleActionItemsClick = useCallback(() => {
    navigate(
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.ACTION_ITEMS}`,
    );
  }, [navigate, projectId, serviceName]);

  const cardData = useMemo(
    () => [
      {
        title: t('meeting-secretary.metrics.action-items.title'),
        description: t('meeting-secretary.metrics.action-items.description'),
        value: statistics?.totalActionItems ?? 0,
        isError,
        onClick: handleActionItemsClick,
      },
      {
        title: t('meeting-secretary.metrics.total-meetings.title'),
        description: t('meeting-secretary.metrics.total-meetings.description'),
        value: statistics?.totalMeetings ?? 0,
        isError,
      },
      {
        title: t('meeting-secretary.metrics.meeting-hours.title'),
        description: t('meeting-secretary.metrics.meeting-hours.description'),
        value: statistics?.totalMeetingHours ?? 0,
        isError,
      },
    ],
    [t, statistics, isError, handleActionItemsClick],
  );

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {cardData.map((card) => (
        <MetricCard
          key={card.title}
          title={card.title}
          description={card.description}
          value={card.value}
          isLoading={showSkeleton}
          isError={card.isError}
          onClick={card.onClick}
        />
      ))}
    </div>
  );
};

export const MeetingSecretaryDashboard = () => {
  const { t } = useTranslation();
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  const handleMeetingSelect = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
  };

  const handleBackToDashboard = () => {
    setSelectedMeeting(null);
  };

  return (
    <div className="flex-1 overflow-auto bg-background p-12">
      {selectedMeeting ? (
        <div>
          <button
            onClick={handleBackToDashboard}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900 cursor-pointer"
          >
            <span className="mr-2">←</span>
            {t('meeting-secretary.meetings.back-to-dashboard')}
          </button>
          <MeetingDetail meeting={selectedMeeting} />
        </div>
      ) : (
        <>
          <MeetingSecretaryHeader />
          <div className="mt-8">
            <MetricsCards />
          </div>
          <div className="mt-12">
            <RecentMeetings onMeetingSelect={handleMeetingSelect} />
          </div>
        </>
      )}
    </div>
  );
};
